<template>
  <div>
    <v-card color="cardbackground">
      <v-card-title class="pt-2">
        Line
      </v-card-title>
      <v-card-text>
        <v-btn
          block
          color="primary"
          :disabled="!isOnline || disabled"
          @click="openMainDialog()">
          <v-icon class="mr-2">mdi-book-open-outline</v-icon>
          Open Editor
        </v-btn>
      </v-card-text>
    </v-card>

    <v-dialog
      v-model="showMainDialog"
      max-width="600px"
      transition="dialog-bottom-transition"
      persistent>
      <v-card flat color="cardbackground">
        <v-card-text class="pa-4">
          <egg-dialog-title
            title="Line"
            subtitle="editor"
            @close-clicked="closeMainDialog()"
          />
          <v-card flat color="cardbackground">
            <v-card-text>
              <v-card class="py-1 px-4 mb-2">
                <v-data-table
                  fixed-header
                  :search="search"
                  :headers="lookupItemHeaders"
                  :items="formattedLookupItems"
                  no-data-text="No Data Available">
                  <template v-slot:top>
                    <div class="mb-0 d-flex flex-wrap overflow-y-auto">
                      <v-text-field
                        class="mt-1"
                        v-model="search"
                        clearable
                        label="Search"
                        type="text"
                        prepend-icon="mdi-magnify"
                      />
                      <egg-action-buttons
                        class="mt-2 ml-4"
                        primary-button-icon="mdi-plus"
                        @primary-button-clicked="addItem()"
                        primary-button-text="Add"
                        show-large-buttons
                      />
                    </div>
                  </template>
                  <template v-slot:[`item.backgroundColor`]="{ item }">
                    <v-chip small :text-color="`#${item.textColor}`" :color="`#${item.backgroundColor}`">{{ item.backgroundColor }}</v-chip>
                  </template>
                  <template v-slot:[`item.isEnabled`]="{ item }">
                    <div class="d-flex justify-center">
                      <v-icon v-if="item.isEnabled">mdi-checkbox-marked-outline</v-icon>
                      <v-icon v-else>mdi-checkbox-blank-outline</v-icon>
                    </div>
                  </template>
                  <template v-slot:[`item.action`]="{ item }">
                    <egg-action-buttons
                      primary-button-icon="mdi-square-edit-outline"
                      @primary-button-clicked="editItem(item)"
                      secondary-button-icon="mdi-trash-can-outline"
                      @secondary-button-clicked="confirmDeleteItem(item)"/>
                  </template>
                </v-data-table>
              </v-card>
            </v-card-text>
          </v-card>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-form ref="form" v-model="validForm">
      <v-dialog v-model="showChangeDialog" 
        scrollable 
        persistent
        max-width="400px">
        <v-card>
          <v-card-title class="text-h5">
             {{isAdd ? 'Add' : 'Edit'}} Line
          </v-card-title>
          <v-card-text class="pb-4">
            <v-row>
              <v-col cols="12" sm="6">
                <v-select
                  v-model="editedItem.farmCode"
                  :disabled="!isAdd"
                  label="Farms"
                  :items="farms"
                  :rules="[rules.required]"
                />
                 <v-text-field
                  dark
                  class="pt-3 cep-field-style"
                  dense
                  v-model="editedItem.lineCode"
                  :disabled="!isAdd"
                  label="Line"
                  :style="`--lineTextColor:#${editedItemTextColor}`"
                  :background-color="editedItemBackgroundColor"
                  :items="lines"
                  :rules="[rules.required, rules.numeric]"
                />

                <v-checkbox 
                  v-model="editedItem.isEnabled" 
                  label="Active" />
              </v-col>

              <v-col cols="12" sm="6">
                <v-select
                  label="Line Text Color"
                  v-model="editedItemTextColor"
                  :items="lineTextColors"
                />

                <div style="font-size:13px">Line Background Color</div>
                <v-color-picker
                  v-model="editedItemBackgroundColor"
                  mode="hexa"
                />
              </v-col>
            </v-row>
          </v-card-text>
          <egg-card-actions class="pt-0 pb-5"
            primary-button-text="Save"
            secondary-button-text="Cancel"
            :disabled="!validForm"
            @primary-button-clicked="saveItem()"
            @secondary-button-clicked="closeChangeDialog()" />
        </v-card>
      </v-dialog>
      <v-dialog v-model="showDeleteDialog" persistent max-width="350">
        <v-card>
          <v-card-title class="text-h5">
            Confirm Delete
          </v-card-title>
          <v-card-text>
             Ok to Delete 'Farm: {{itemToDelete.farmCode}} Line:{{itemToDelete.lineCode}}'?
          </v-card-text>
          <egg-card-actions
            primary-button-text="Yes"
            secondary-button-text="No"
            @primary-button-clicked="deleteItem()"
            @secondary-button-clicked="showDeleteDialog = false" />
        </v-card>
      </v-dialog>
    </v-form>
  </div>
</template>

<script>
import DialogTitle from './DialogTitle'
import ActionButtons from './ActionButtons'
import CardActions from './CardActions'
import eggApi from '../api/eggApi'
import { mapState } from 'vuex'

export default {
  props: {
    disabled: {
      type: Boolean,
      default: true
    },
  },
  created() {
    this.rules = { ...this.$root.validationRules };
  },
  components: {
    'egg-dialog-title': DialogTitle,
    'egg-action-buttons': ActionButtons,
    'egg-card-actions': CardActions
  },
  data() {
    return {
      editedItem: {},
      editedItemBackgroundColor: null,
      editedItemTextColor: null,
      isAdd: false,
      itemToDelete: {},
      lookupItems: [],
      rules: null,
      search: '',
      showChangeDialog: false,
      showDeleteDialog: false,
      showMainDialog: false,
      validForm: true
    }
  },
  computed: {
    ...mapState(['settings','isOnline']),
    farms() {
      return this.settings.farms.map(x=> x.farmCode);
    },
    formattedLookupItems() {
      var items = JSON.parse(JSON.stringify(this.lookupItems));
      items.forEach(b => b.lineCode = (b.lineCode ? 'L' + b.lineCode : b.lineCode));
      return items;
    },
    lines() {
      return this.lookupItems
          .filter(x => x.farmCode == this.editedItem.farmCode)
          .map(x=> "L" + x.lineCode);
    },
    lineTextColors() {
      return [ 
        {text: 'White', value: 'FFFFFFFF'},
        {text: 'Black', value: '000000FF'}
      ];
    },
    lookupItemHeaders() {
      return [
        { text: 'Farm', value: 'farmCode', sortable: true, align: 'center' },
        { text: 'Line', value: 'lineCode', sortable: true, align: 'center' },
        { text: 'Active', value: 'isEnabled', sortable: true, align: 'center' },
        { text: 'Color', value: 'backgroundColor', sortable: true, align: 'center' },
        { text: 'Actions', value: 'action', sortable: false, align: 'center' }
      ];
    }
  },
  methods: {
    addItem() {
      this.$refs.form.resetValidation();
      this.isAdd = true;
      this.editedItem = {
        isEnabled: true
      };
      this.editedItemBackgroundColor ="205075FF";
      this.editedItemTextColor = "FFFFFFFF";
      this.showChangeDialog = true;
    },
    closeChangeDialog() {
      this.showChangeDialog = false;
    },
    closeMainDialog() {
      this.showMainDialog = false;
    },
    confirmDeleteItem(item) {
      this.itemToDelete = item;
      this.showDeleteDialog = true;
    },
    async deleteItem() {
      await eggApi.deleteFarmLine(this.itemToDelete.farmCode, this.itemToDelete.lineCode);
      this.lookupItems = await eggApi.getFarmLines(false);
      this.showDeleteDialog = false;
    },
    editItem(item) {
      this.$refs.form.resetValidation();
      this.isAdd = false;
      this.editedItem = {
        farmCode: item.farmCode,
        lineCode: item.lineCode.replace('L',''),
        isEnabled: item.isEnabled
      }
      this.editedItemBackgroundColor = item.backgroundColor;
      this.editedItemTextColor  = item.textColor;

      this.showChangeDialog = true;
    },
    async openMainDialog() {
      this.lookupItems = await eggApi.getFarmLines(false);
      this.showMainDialog = true;
    },
    async saveItem() {
      let payload = {
        farmCode: this.editedItem.farmCode,
        lineCode: this.editedItem.lineCode.replace('L', '').trim(),
        backgroundColor: this.editedItemBackgroundColor.replace('#',''),
        textColor: this.editedItemTextColor.replace('#', ''),
        isEnabled: this.editedItem.isEnabled
      };

      if (this.isAdd) {
        await eggApi.addFarmLine(payload);
      } else {
        await eggApi.updateFarmLine(payload);
      }
      this.lookupItems = await eggApi.getFarmLines(false);
      this.closeChangeDialog();
    }
  }
}
</script>
<style scoped>
 .cep-field-style >>> .v-text-field__slot input {
    color: var(--lineTextColor);
  }
</style>